var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CompassColor, IconButton, H2 } from '@noom/wax-component-library';
import styled from 'styled-components';
import useMeasure from 'react-use/lib/useMeasure';
import useScroll from 'react-use/lib/useScroll';
const ITEM_SPACE_SIZE = 16;
const DEFAULT_COLUMN_NUMBER = {
    1024: 2,
    1280: 3,
    1440: 4,
    1800: 5,
};
const Wrap = styled.div `
  position: relative;
`;
const Header = styled.div `
  display: flex;
  justify-content: space-between;
  margin: 0 0 16px 0;
  align-items: flex-end;
  flex-wrap: wrap;
  padding: 0 1rem;
`;
const SubTitle = styled.div `
  ${({ theme }) => theme.typography.body};
  flex-basis: 100%;
`;
const PaginationButton = (_a) => {
    var { onClick, left, right } = _a, props = __rest(_a, ["onClick", "left", "right"]);
    return (React.createElement(Box, { zIndex: 1, position: "absolute", top: 0, onClick: onClick, h: "100%", display: "flex", alignItems: "center", left: left, right: right },
        React.createElement(IconButton, Object.assign({ boxShadow: "xl", bg: CompassColor.offWhite, color: CompassColor.gold, borderRadius: "full", size: "lg", marginX: 2, iconProps: {
                fontSize: '2.5rem',
            } }, props))));
};
const ScrollContainer = styled.div `
  overflow-x: hidden;
`;
const StretchedList = styled.div `
  margin-bottom: 0.188rem; // give the shadow a little space
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 100%;
  grid-gap: ${ITEM_SPACE_SIZE}px;

  ${({ columns }) => Object.entries(columns).map(([breakpoint, column]) => `
        @media (min-width: ${breakpoint}px) {
        grid-auto-columns: calc((100% / ${column}) - (${ITEM_SPACE_SIZE}px * ${column - 1} / ${column}));
    }
  `)} );
`;
const HorizontalList = ({ title = '', subTitle, children, columns = DEFAULT_COLUMN_NUMBER, hasMore = false, loadMore = () => { }, }) => {
    var _a, _b;
    const containerRef = useRef(null);
    const { x: scrollPosition } = useScroll(containerRef);
    const [wrapperRef, { width }] = useMeasure();
    const [page, setPage] = useState(0);
    const contentWidth = (_b = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.scrollWidth) !== null && _b !== void 0 ? _b : 0;
    const hasMultiPage = useMemo(() => contentWidth > width, [contentWidth, width]);
    const isLastPage = useMemo(() => scrollPosition >= contentWidth - width, [scrollPosition, contentWidth, width]);
    const isFirstPage = useMemo(() => scrollPosition === 0, [scrollPosition]);
    useEffect(() => {
        var _a;
        return (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo({
            left: (width + ITEM_SPACE_SIZE) * page,
            behavior: 'smooth',
        });
    }, [width, page]);
    useEffect(() => {
        if (scrollPosition >= contentWidth - width * 2 && hasMore) {
            loadMore();
        }
    }, [scrollPosition, contentWidth, width, hasMore, loadMore]);
    return (React.createElement(Wrap, { ref: wrapperRef },
        React.createElement(Header, null,
            React.createElement(H2, null, title),
            subTitle && React.createElement(SubTitle, null, subTitle)),
        React.createElement(ScrollContainer, { ref: containerRef, page: page },
            hasMultiPage && (React.createElement(React.Fragment, null,
                React.createElement(PaginationButton, { left: 0, isDisabled: isFirstPage, onClick: () => setPage(page - 1), icon: "chevron-left" }),
                React.createElement(PaginationButton, { right: 0, isDisabled: isLastPage, onClick: () => setPage(page + 1), icon: "chevron-right" }))),
            React.createElement(StretchedList, { columns: columns }, children))));
};
export default HorizontalList;
//# sourceMappingURL=index.js.map